import { Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatCurrency, toTitleCase } from '../lib/utils';
import { Badge } from './Badge';
import { Button } from './Button';
import { ReviewHintIcon } from './ReviewHintIcon';
import { mortgageNeedsPlanningReview, type MortgagePlanningReviewLiability } from './MortgagePlanningReviewSheet';

type ConnectionAccount = {
  id: string;
  name: string;
  mask: string | null;
  type: string;
  subtype: string | null;
  balance: number | string | null;
  provider?: string;
  needsTaxSubtypeReview?: boolean;
  excludeFromNetWorthAndFire?: boolean;
};

type ConnectionAccountRowsProps = {
  accounts: ConnectionAccount[];
  liabilitiesByAccountId?: Map<string, MortgagePlanningReviewLiability>;
  onReviewTaxAccount?: (accountId: string) => void;
  onReviewMortgage?: (liabilityId: string) => void;
  className?: string;
};

function getAccountTypeLabel(account: ConnectionAccount) {
  if (account.type === 'depository') return 'Cash';
  if (account.type === 'investment') return 'Investment';
  if (account.type === 'credit') return 'Credit';
  if (account.type === 'loan') return 'Loan';
  return toTitleCase(account.type);
}

export function ConnectionAccountRows({
  accounts,
  liabilitiesByAccountId,
  onReviewTaxAccount,
  onReviewMortgage,
  className = '',
}: ConnectionAccountRowsProps) {
  if (accounts.length === 0) return null;

  return (
    <View className={`gap-2 ${className}`}>
      {accounts.map((account) => {
        const liability = liabilitiesByAccountId?.get(account.id);
        const isMortgage = liability?.liabilityType === 'mortgage';
        const mortgageMissingDetails = liability
          ? mortgageNeedsPlanningReview(liability)
          : false;
        return (
          <View
            key={account.id}
            className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-2"
          >
            <View className="flex-row items-start justify-between gap-3">
              <View className="flex-1">
                <View className="flex-row flex-wrap items-center gap-2">
                  <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {account.name}
                  </Text>
                  {isMortgage ? (
                    <Badge label="Mortgage" variant="blue" />
                  ) : (
                    <Badge label={getAccountTypeLabel(account)} variant="gray" />
                  )}
                </View>
                <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {[
                    account.mask ? `••••${account.mask}` : null,
                    account.subtype ? toTitleCase(account.subtype) : null,
                    account.excludeFromNetWorthAndFire ? 'Excluded from FIRE' : null,
                  ].filter(Boolean).join(' · ') || 'Linked account'}
                </Text>
                {mortgageMissingDetails ? (
                  <View className="mt-2 flex-row items-center gap-1.5">
                    <Ionicons name="warning-outline" size={12} color="#d97706" />
                    <Text className="flex-1 text-xs text-amber-700 dark:text-amber-400">
                      Add P&I and escrow details for FIRE planning.
                    </Text>
                  </View>
                ) : isMortgage && liability ? (
                  <Text className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    P&I {formatCurrency(liability.principalAndInterestMonthly ?? 0, { decimals: 0 })}/mo
                  </Text>
                ) : null}
              </View>

              <View className="items-end gap-2">
                <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {formatCurrency(Number(account.balance ?? 0), { decimals: 0 })}
                </Text>
                {account.needsTaxSubtypeReview && onReviewTaxAccount ? (
                  <ReviewHintIcon
                    tooltip="Account type needs review"
                    onPress={() => onReviewTaxAccount(account.id)}
                  />
                ) : null}
              </View>
            </View>

            {mortgageMissingDetails && liability && onReviewMortgage ? (
              <View className="mt-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() => onReviewMortgage(liability.id)}
                >
                  Add mortgage details
                </Button>
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}
